import { useState, type ReactNode } from 'react';
import { CalendarRange, Clock, Info } from 'lucide-react';
import { Carte, Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { useDeclarationFormateur } from '../../lib/useFormateurs';
import type {
  EcartHeures,
  EtatDeclaration,
  HeuresPrevues,
  MoisDeclarationPropose,
  RepereHeures,
  VueDeclaration,
} from '../../lib/useEspaceFormateur';
import { ChoixMois, LigneApprenant, LigneSession, ListeVide } from './PrevisionHeures';

/**
 * Heures réalisées du formateur (TF-420) : ce que partagent sa déclaration
 * (onglet « Heures réalisées » de « Mes heures ») et la lecture du pôle dans
 * « Formateurs ». Calendrier, écart et repère viennent de l'API.
 */

const TONS_ETAT: Record<EtatDeclaration, string> = {
  A_VENIR: 'neutral',
  A_DECLARER: 'info',
  EN_RETARD: 'danger',
  DECLAREE: 'success',
  FIGEE: 'neutral',
  HORS_SERVICE: 'neutral',
};

export function PastilleEtatDeclaration({ etat, libelle }: { etat: EtatDeclaration; libelle: string }): JSX.Element {
  return <Pastille texte={libelle} ton={TONS_ETAT[etat]} />;
}

export function SelecteurMoisDeclaration({
  id,
  mois,
  proposes,
  onChange,
}: {
  id: string;
  mois: string;
  proposes: MoisDeclarationPropose[];
  onChange: (mois: string) => void;
}): JSX.Element {
  return (
    <ChoixMois
      id={id}
      mois={mois}
      options={proposes.map((propose) => ({ cle: propose.cle, libelle: propose.libelle, etat: propose.etatLibelle }))}
      onChange={onChange}
    />
  );
}

const TONS_ECART: Record<EcartHeures['sens'], string> = {
  EGAL: 'text-codialis-gray',
  PLUS: 'text-codialis-sky',
  MOINS: 'text-amber-700',
};

export function TexteEcart({ ecart }: { ecart: EcartHeures | null }): JSX.Element {
  if (!ecart) return <span className="text-sm text-codialis-gray">—</span>;
  return <span className={`text-sm font-bold ${TONS_ECART[ecart.sens]}`}>{ecart.texte}</span>;
}

/** « Repère : 28 h émargées sur vos séances » — rien n'est pré-rempli à partir de lui. */
export function MentionRepere({ repere, vous = true }: { repere: RepereHeures; vous?: boolean }): JSX.Element {
  return (
    <p className="mt-1 flex items-start gap-1 text-xs text-codialis-gray">
      <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="min-w-0">
        Repère : {repere.texte} sur {vous ? 'vos' : 'ses'} séances du mois
      </span>
    </p>
  );
}

function Colonne({ titre, children }: { titre: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-codialis-gray">{titre}</div>
      {/* La hauteur de la case : une valeur lue s'aligne sur la case saisie de la colonne voisine. */}
      <div className="flex min-h-[44px] items-center">
        <div className="w-full min-w-0">{children}</div>
      </div>
    </div>
  );
}

/** Prévu, réalisé (lu ou saisi), écart : trois colonnes, côte à côte dès le mobile. */
export function ColonnesHeures({
  prevu,
  realise,
  ecart,
  empilee = false,
}: {
  prevu: HeuresPrevues | null;
  realise: ReactNode;
  ecart: EcartHeures | null;
  /** Sous la cible : alignées à gauche, comme elle. */
  empilee?: boolean;
}): JSX.Element {
  return (
    <div className={`grid grid-cols-3 items-start gap-3 ${empilee ? '' : 'md:text-right'}`}>
      <Colonne titre="Prévu">
        <span className={`block text-sm font-bold ${prevu ? 'text-codialis-navy' : 'text-codialis-gray'}`}>
          {prevu?.texte ?? '—'}
        </span>
      </Colonne>
      <Colonne titre="Réalisé">{realise}</Colonne>
      <Colonne titre="Écart">
        <TexteEcart ecart={ecart} />
      </Colonne>
    </div>
  );
}

/** Le réalisé en lecture, dans sa colonne. */
export function RealiseLu({ texte }: { texte: string | null }): JSX.Element {
  return (
    <span className={`block text-sm font-bold ${texte ? 'text-codialis-navy' : 'text-codialis-gray'}`}>{texte ?? '—'}</span>
  );
}

/** « Déclarée le 03/11/2026 à 09:12 · corrigée le 15/11/2026 à 17:40 » */
export function texteDeclaration(declaration: VueDeclaration['declaration']): string | null {
  if (!declaration) return null;
  const instant = (iso: string): string =>
    new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Indian/Reunion',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
      .format(new Date(iso))
      .replace(' ', ' à ');
  const declaree = `Déclarée le ${instant(declaration.declareeLe)}`;
  return declaration.corrigeeLe ? `${declaree} · corrigée le ${instant(declaration.corrigeeLe)}` : declaree;
}

/**
 * Lecture du pôle, dans la fiche du formateur : ce qu'il a déclaré en face
 * de son prévisionnel, et l'écart. Le pôle ne déclare pas à sa place.
 */
export function CarteDeclarationFormateur({ formateurId }: { formateurId: string }): JSX.Element {
  const [mois, setMois] = useState<string | null>(null);
  const declaration = useDeclarationFormateur(formateurId, mois);
  const vue = declaration.data;
  const vide = vue && vue.sessions.length === 0 && vue.apprenants.length === 0;
  const declaree = vue ? texteDeclaration(vue.declaration) : null;

  return (
    <Carte
      titre="Heures réalisées"
      actions={
        vue ? (
          <>
            <PastilleEtatDeclaration etat={vue.etat} libelle={vue.etatLibelle} />
            <SelecteurMoisDeclaration
              id={`mois-declaration-${formateurId}`}
              mois={vue.mois}
              proposes={vue.moisProposes}
              onChange={setMois}
            />
          </>
        ) : undefined
      }
      sansBordure
    >
      {declaration.isLoading && (
        <div className="p-4 sm:p-6">
          <Chargement />
        </div>
      )}
      {declaration.isError && (
        <div className="p-4 sm:p-6">
          <MessageErreur message="Les heures réalisées n’ont pas pu être lues." />
        </div>
      )}
      {vue && (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-gray-100 px-4 py-3 text-sm sm:px-6">
            <span className="inline-flex items-center gap-1.5 text-codialis-gray">
              <CalendarRange className="h-4 w-4 shrink-0" aria-hidden />
              {vue.libelle}
            </span>
            <span className="text-codialis-gray">
              Prévu <strong className="text-codialis-navy">{vue.total.prevu.texte}</strong>
            </span>
            <span className="text-codialis-gray">
              Réalisé <strong className="text-codialis-navy">{vue.total.realise?.texte ?? '—'}</strong>
            </span>
            <span className="text-codialis-gray">
              Écart <TexteEcart ecart={vue.total.ecart} />
            </span>
          </div>
          <p className="border-b border-gray-100 px-4 py-2.5 text-xs text-codialis-gray sm:px-6">
            {declaree ?? (vue.etat === 'A_VENIR' || vue.etat === 'HORS_SERVICE' ? vue.avis : 'Pas encore déclarée.')}
            {' · '}Émargé sur ses séances : {vue.total.repere.texte}.
          </p>
          {vide ? (
            <ListeVide>Ni prévision ni déclaration pour {vue.libelle}.</ListeVide>
          ) : (
            <ul className="divide-y divide-gray-100">
              {vue.sessions.map((ligne) => (
                <LigneSession
                  key={ligne.sessionId}
                  ligne={ligne}
                  formateur={false}
                  empilee
                  complement={<MentionRepere repere={ligne.repere} vous={false} />}
                  heures={
                    <ColonnesHeures
                      empilee
                      prevu={ligne.prevu}
                      ecart={ligne.ecart}
                      realise={<RealiseLu texte={ligne.realise?.texte ?? null} />}
                    />
                  }
                />
              ))}
              {vue.apprenants.map((ligne) => (
                <LigneApprenant
                  key={ligne.apprenantId}
                  ligne={ligne}
                  formateur={false}
                  empilee
                  complement={<MentionRepere repere={ligne.repere} vous={false} />}
                  heures={
                    <ColonnesHeures
                      empilee
                      prevu={ligne.prevu}
                      ecart={ligne.ecart}
                      realise={<RealiseLu texte={ligne.realise?.texte ?? null} />}
                    />
                  }
                />
              ))}
            </ul>
          )}
          {vue.lignesNonAffichees > 0 && (
            <p className="flex gap-2 border-t border-gray-100 px-4 py-3 text-xs text-codialis-gray sm:px-6">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
              <span className="min-w-0">
                {vue.lignesNonAffichees} ligne{vue.lignesNonAffichees > 1 ? 's' : ''} d’une autre antenne
                {vue.lignesNonAffichees > 1 ? ' ne sont' : ' n’est'} pas affichée{vue.lignesNonAffichees > 1 ? 's' : ''} :
                elle{vue.lignesNonAffichees > 1 ? 's sont' : ' est'} hors de votre périmètre.
              </span>
            </p>
          )}
        </>
      )}
    </Carte>
  );
}
